import React from 'react';
import { profileTypeMapping } from '@/utils/profileTypeMapping';
import { PROFILE_CATEGORIES, ProfileCategory } from '@/utils/profileCategories';

interface ScoreData {
  name: string;
  fullName: string;
  value: number;
  color: string;
  sampleSize?: number;
}

interface PrintableProfilesContentProps {
  profilesData: {
    pattern: string;
    profileData: {
      type: string;
      description: string;
      traits: string[];
      workStyle: string;
      communication: string;
      category: ProfileCategory;
    };
    scores: ScoreData[];
    isRealData: boolean;
  }[];
}

const PrintableProfilesContent: React.FC<PrintableProfilesContentProps> = ({ profilesData }) => {
  // Group profiles by category
  const profilesByCategory: Record<ProfileCategory, typeof profilesData> = {
    GAS: [],
    GLUE: [],
    BRAKE: []
  };

  profilesData.forEach(profile => {
    const category = profile.profileData.category;
    profilesByCategory[category].push(profile);
  });

  const renderChart = (scores: ScoreData[]) => {
    const chartWidth = 220;
    const chartHeight = 110;
    const labelWidth = 20;
    const plotLeft = labelWidth + 5;
    const plotRight = chartWidth - 10;
    const plotWidth = plotRight - plotLeft;
    const rowHeight = 18;
    const topPadding = 14;

    // Get D-C-T-O scores (main group) and S separately
    const mainTraits = ['D', 'C', 'T', 'O'];
    const mainScores = mainTraits
      .map(name => scores.find(s => s.name === name))
      .filter(Boolean) as ScoreData[];
    const sScore = scores.find(s => s.name === 'S');

    if (mainScores.length === 0) return null;

    const getX = (value: number) => plotLeft + (value / 100) * plotWidth;
    const getY = (index: number) => topPadding + index * rowHeight + rowHeight / 2;

    return (
      <svg width={chartWidth} height={chartHeight} style={{ backgroundColor: '#fafafa', borderRadius: '6px', border: '1px solid #e5e5e5' }}>
        {/* X-axis labels */}
        <text x={plotLeft} y={10} fontSize="8" fill="#999" textAnchor="middle">0%</text>
        <text x={plotLeft + plotWidth / 2} y={10} fontSize="8" fill="#999" textAnchor="middle">50%</text>
        <text x={plotRight} y={10} fontSize="8" fill="#999" textAnchor="middle">100%</text>

        {/* 50% reference line */}
        <line
          x1={plotLeft + plotWidth / 2}
          y1={topPadding}
          x2={plotLeft + plotWidth / 2}
          y2={topPadding + mainScores.length * rowHeight}
          stroke="#ddd"
          strokeWidth="1"
          strokeDasharray="3,3"
        />

        {/* Horizontal track lines for each trait */}
        {mainScores.map((_, i) => (
          <line
            key={`track-${i}`}
            x1={plotLeft}
            y1={getY(i)}
            x2={plotRight}
            y2={getY(i)}
            stroke="#e8e8e8"
            strokeWidth="1"
          />
        ))}

        {/* Connecting line between D-C-T-O dots */}
        <polyline
          points={mainScores.map((score, i) => `${getX(score.value)},${getY(i)}`).join(' ')}
          fill="none"
          stroke="#666"
          strokeWidth="1.5"
        />

        {/* Main trait dots and labels */}
        {mainScores.map((score, i) => (
          <g key={score.name}>
            <text x={8} y={getY(i) + 3} fontSize="9" fill="#555" fontWeight="600" textAnchor="middle">
              {score.name}
            </text>
            <circle cx={getX(score.value)} cy={getY(i)} r="5" fill={score.color} stroke="#fff" strokeWidth="1.5" />
          </g>
        ))}

        {/* Separator line before S */}
        <line
          x1={plotLeft}
          y1={topPadding + mainScores.length * rowHeight + 4}
          x2={plotRight}
          y2={topPadding + mainScores.length * rowHeight + 4}
          stroke="#ddd"
          strokeWidth="1"
        />

        {/* S trait (separate) */}
        {sScore && (
          <g>
            <line
              x1={plotLeft}
              y1={getY(mainScores.length) + 8}
              x2={plotRight}
              y2={getY(mainScores.length) + 8}
              stroke="#e8e8e8"
              strokeWidth="1"
            />
            <text x={8} y={getY(mainScores.length) + 11} fontSize="9" fill="#555" fontWeight="600" textAnchor="middle">
              S
            </text>
            <circle cx={getX(sScore.value)} cy={getY(mainScores.length) + 8} r="5" fill={sScore.color} stroke="#fff" strokeWidth="1.5" />
          </g>
        )}
      </svg>
    );
  };

  return (
    <div 
      id="printable-profiles-content"
      style={{
        width: '800px',
        backgroundColor: '#ffffff',
        padding: '30px',
        fontFamily: 'system-ui, -apple-system, sans-serif',
      }}
    >
      {/* Header */}
      <div style={{ marginBottom: '30px', borderBottom: '2px solid #333', paddingBottom: '15px' }}>
        <h1 style={{ fontSize: '24px', fontWeight: '600', margin: 0, color: '#111' }}>
          Behavioral Profile Templates
        </h1>
        <p style={{ fontSize: '12px', color: '#666', marginTop: '5px' }}>
          Generated on {new Date().toLocaleDateString()}
        </p>
      </div>

      {/* Categories */}
      {(['GAS', 'GLUE', 'BRAKE'] as ProfileCategory[]).map((category) => {
        const profiles = profilesByCategory[category];
        const categoryInfo = PROFILE_CATEGORIES[category];

        return (
          <div key={category} style={{ marginBottom: '30px' }}>
            {/* Category Header */}
            <div style={{ 
              marginBottom: '15px', 
              padding: '8px 12px',
              backgroundColor: categoryInfo.color + '20',
              borderLeft: `4px solid ${categoryInfo.color}`,
              borderRadius: '0 4px 4px 0'
            }}>
              <h2 style={{ fontSize: '16px', fontWeight: '600', margin: 0, color: '#333' }}>
                {categoryInfo.emoji} {categoryInfo.name}
              </h2>
              <p style={{ fontSize: '11px', color: '#666', margin: '3px 0 0 0' }}>
                {categoryInfo.description}
              </p>
            </div>

            {/* Profiles in this category */}
            {profiles.map((profile, index) => (
              <div 
                key={profile.pattern}
                style={{
                  marginBottom: '20px',
                  padding: '15px',
                  border: '1px solid #e0e0e0',
                  borderRadius: '6px',
                  backgroundColor: '#fafafa',
                  pageBreakInside: 'avoid'
                }}
              >
                {/* Profile Header */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '10px' }}>
                  <div>
                    <h3 style={{ fontSize: '14px', fontWeight: '600', margin: 0, color: '#222' }}>
                      {profile.profileData.type}
                    </h3>
                    <span style={{ 
                      fontSize: '10px', 
                      fontFamily: 'monospace',
                      backgroundColor: '#e8e8e8',
                      padding: '2px 6px',
                      borderRadius: '3px',
                      color: '#555'
                    }}>
                      {profile.pattern}
                    </span>
                  </div>
                  {renderChart(profile.scores)}
                </div>

                {/* Description */}
                <p style={{ fontSize: '11px', color: '#555', margin: '10px 0', lineHeight: '1.5' }}>
                  {profile.profileData.description}
                </p>

                {/* Work Style & Communication */}
                <div style={{ display: 'flex', gap: '20px', marginTop: '10px' }}>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '10px', fontWeight: '600', color: '#444', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                      Work Style
                    </h4>
                    <p style={{ fontSize: '10px', color: '#666', margin: 0, lineHeight: '1.4' }}>
                      {profile.profileData.workStyle}
                    </p>
                  </div>
                  <div style={{ flex: 1 }}>
                    <h4 style={{ fontSize: '10px', fontWeight: '600', color: '#444', margin: '0 0 4px 0', textTransform: 'uppercase' }}>
                      Communication
                    </h4>
                    <p style={{ fontSize: '10px', color: '#666', margin: 0, lineHeight: '1.4' }}>
                      {profile.profileData.communication}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        );
      })}

      {/* Footer */}
      <div style={{ 
        marginTop: '30px', 
        paddingTop: '15px', 
        borderTop: '1px solid #ddd',
        textAlign: 'center'
      }}>
        <p style={{ fontSize: '10px', color: '#999' }}>
          © {new Date().getFullYear()} Zentrix Insights • Behavioral Profile Templates
        </p>
      </div>
    </div>
  );
};

export default PrintableProfilesContent;
